import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchBriefingWeather,
  weatherCodeToIconKey,
  formatForecastDate,
  formatTempRange,
  formatTemp,
  formatRainChance,
  formatHourLabel,
  isWeatherStale,
  buildWeatherTimeline,
  isDayCollapsed,
  type DayDoors,
  type DoorsWindow,
  type WeatherDay,
  type HourlyForecast,
  type WeatherIconKey,
} from "@produce/shared";
import {
  Cloud,
  Sun,
  CloudSun,
  CloudFog,
  CloudDrizzle,
  CloudRain,
  CloudSnow,
  CloudLightning,
  Droplet,
  ChevronDown,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";

// Shared module owns the weather-code → semantic key logic; this map is the
// web-only binding of key → lucide-react icon (mobile has its own binding).
const ICONS: Record<WeatherIconKey, LucideIcon> = {
  sun: Sun,
  "cloud-sun": CloudSun,
  cloud: Cloud,
  "cloud-fog": CloudFog,
  "cloud-drizzle": CloudDrizzle,
  "cloud-rain": CloudRain,
  "cloud-snow": CloudSnow,
  "cloud-lightning": CloudLightning,
};

function WeatherIcon({ code }: { code: number }) {
  const Icon = ICONS[weatherCodeToIconKey(code)];
  return <Icon className="h-5 w-5 text-primary" />;
}

interface WeatherCardProps {
  briefingId: string | null | undefined;
  /** Trigger a server refresh when the cache is missing/stale (default true). */
  enableRefresh?: boolean;
  /** When false, show an "add a navigation address" hint (wizard). */
  hasLocation?: boolean;
  /** Per-day doors overrides (briefing_day_doors rows). */
  perDayDoors?: DayDoors[];
  /** Briefing-level doors fallback. */
  fallbackDoors?: DoorsWindow;
  /** Allow collapsing each day, remembered per day (producer view). */
  collapsible?: boolean;
  /** Typographic scale: "section" inside the wizard, "page" on the
   * read-only briefing pages. */
  variant?: WeatherVariant;
}

export function WeatherCard({
  briefingId,
  enableRefresh = true,
  hasLocation = true,
  perDayDoors = [],
  fallbackDoors,
  collapsible = false,
  variant = "section",
}: WeatherCardProps) {
  const queryClient = useQueryClient();
  const triggeredRef = useRef(false);

  const { data: weather, isLoading } = useQuery({
    queryKey: ["briefing-weather", briefingId],
    enabled: !!briefingId && hasLocation,
    queryFn: () => fetchBriefingWeather(supabase, briefingId!),
  });

  useEffect(() => {
    if (!enableRefresh || !hasLocation || !briefingId || triggeredRef.current) return;
    const needsRefresh =
      !weather || !weather.hourly?.length || isWeatherStale(weather.fetched_at, Date.now());
    if (!needsRefresh) return;
    triggeredRef.current = true;
    (async () => {
      try {
        await supabase.functions.invoke("refresh-weather", {
          body: { briefing_id: briefingId },
        });
        await queryClient.invalidateQueries({ queryKey: ["briefing-weather", briefingId] });
      } catch {
        // Cached data still renders; the hourly cron will fill it in.
      }
    })();
  }, [enableRefresh, hasLocation, briefingId, weather, queryClient]);

  // Timeline assembly (doors trim, group, drop empty days) lives in the shared
  // module; depend on the doors primitives so an inline fallbackDoors object
  // prop doesn't bust the memo every render.
  const { dayGroups, defaultOpenDate } = useMemo(
    () =>
      buildWeatherTimeline({
        hourly: weather?.hourly,
        nowMs: Date.now(),
        perDayDoors,
        fallbackDoors,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [weather?.hourly, perDayDoors, fallbackDoors?.doors_open, fallbackDoors?.doors_close],
  );

  // Collapse state (persisted to localStorage). Default: today expanded,
  // other days collapsed.
  const storageKey = briefingId ? `weather-collapse:${briefingId}` : null;
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    if (!storageKey || typeof localStorage === "undefined") return {};
    try {
      return JSON.parse(localStorage.getItem(storageKey) ?? "{}");
    } catch {
      return {};
    }
  });
  const isCollapsed = (date: string): boolean =>
    isDayCollapsed(date, { collapsed, defaultOpenDate, dayCount: dayGroups.length, collapsible });
  const toggle = (date: string) => {
    const next = { ...collapsed, [date]: !isCollapsed(date) };
    setCollapsed(next);
    if (storageKey && typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // ignore quota / privacy-mode errors
      }
    }
  };

  const cardClass = variant === "page" ? "bg-card p-8" : "bg-card p-6";

  if (!hasLocation) {
    return (
      <Card className={cardClass}>
        <Header variant={variant} />
        <p className="text-sm text-muted-foreground">
          Add a navigation address to see the forecast for this festival.
        </p>
      </Card>
    );
  }

  if (isLoading || (!weather && !triggeredRef.current)) {
    return (
      <Card className={cardClass}>
        <Header variant={variant} />
        <p className="text-sm text-muted-foreground">Loading forecast…</p>
      </Card>
    );
  }

  if (!weather || (!dayGroups.length && !weather.daily?.length)) {
    return (
      <Card className={cardClass}>
        <Header variant={variant} />
        <p className="text-sm text-muted-foreground">Forecast on the way — check back shortly.</p>
      </Card>
    );
  }

  const dailyByDate = new Map((weather.daily ?? []).map((d: WeatherDay) => [d.date, d]));

  return (
    <Card className={cardClass}>
      <Header variant={variant} />
      {weather.summary && <p className="mb-3 text-sm">{weather.summary}</p>}

      <div className="space-y-4">
        {dayGroups.map((group) => {
          const day = dailyByDate.get(group.date);
          const collapsedNow = collapsible && isCollapsed(group.date);
          const headerRow = (
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-1 text-sm font-semibold">
                {collapsible &&
                  (collapsedNow ? (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ))}
                {formatForecastDate(group.date)}
              </span>
              {day && (
                <span className="text-xs text-muted-foreground">
                  {formatTempRange(day.tempMin, day.tempMax)}
                </span>
              )}
            </div>
          );
          return (
            <div key={group.date}>
              {collapsible ? (
                <button
                  type="button"
                  onClick={() => toggle(group.date)}
                  className="w-full text-left"
                >
                  {headerRow}
                </button>
              ) : (
                headerRow
              )}
              {!collapsedNow && (
                <div className="flex gap-1 overflow-x-auto pb-1">
                  {group.hours.map((h: HourlyForecast) => (
                    <HourCell key={h.time} h={h} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function HourCell({ h }: { h: HourlyForecast }) {
  const rain = formatRainChance(h.precipitationProbability);
  return (
    <div className="flex min-w-[3.25rem] flex-col items-center gap-1 rounded-lg px-1 py-2">
      <span className="text-xs text-muted-foreground">{formatHourLabel(h.label)}</span>
      <WeatherIcon code={h.weatherCode} />
      <span className="text-sm font-semibold">{formatTemp(h.temperature)}</span>
      {/* Hidden below RAIN_CHANCE_DISPLAY_THRESHOLD (formatRainChance → null). */}
      {rain != null && (
        <span className="flex items-center gap-0.5 text-[11px] text-sky-400">
          <Droplet className="h-3 w-3" />
          {rain}
        </span>
      )}
    </div>
  );
}

/**
 * The card sits in two places with different typographic scales: the
 * briefing wizard (section cards: text-lg, p-6) and the read-only
 * briefing pages (page cards: text-2xl, p-8). It used to render one
 * fixed style plus a cloud icon, which made it the odd one out on both
 * — a smaller title than its neighbours, and the only heading on the
 * page with an icon. `variant` lets it match whichever page it is on.
 */
type WeatherVariant = "section" | "page";

function Header({ variant }: { variant: WeatherVariant }) {
  return variant === "page" ? (
    <h2 className="mb-6 text-2xl font-bold">Weather</h2>
  ) : (
    <h3 className="mb-4 text-lg font-semibold">Weather</h3>
  );
}
